import { Link } from "react-router-dom";
import type { Movie } from "@/lib/movieData";

interface MovieCardProps {
  movie: Movie;
  showSummary?: boolean;
  rank?: number;
}

export default function MovieCard({ movie, showSummary = true, rank }: MovieCardProps) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col cursor-pointer"
      aria-label={`查看电影《${movie.title}》详情`}
    >
      <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
        <img
          src={movie.poster}
          alt={`电影《${movie.title}》海报，${movie.year}年${movie.region}${movie.types.join("、")}片`}
          title={`${movie.title} - 秋霞电影`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/75 via-primary-950/5 to-transparent opacity-80 group-hover:opacity-95 transition-opacity"></div>

        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 h-6 px-2 rounded-md bg-primary-950/80 text-accent-300 text-xs font-medium backdrop-blur-sm">
          <i className="ri-star-fill text-[11px]"></i>
          {movie.rating.toFixed(1)}
        </span>

        {rank !== undefined && (
          <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-md bg-accent-500 text-background-50 text-xs font-semibold flex items-center justify-center">
            {rank}
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="flex flex-wrap gap-1">
            {movie.types.slice(0, 2).map((type) => (
              <span
                key={type}
                className="px-1.5 py-0.5 rounded text-[10px] text-background-50 bg-background-50/15 border border-background-50/20"
              >
                {type}
              </span>
            ))}
          </div>
        </div>

        <span className="absolute bottom-2.5 right-2.5 w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <i className="ri-arrow-right-line text-sm"></i>
        </span>
      </div>

      <div className="pt-3">
        <h3 className="font-heading text-base text-foreground-950 leading-snug line-clamp-1 group-hover:text-primary-600 transition-colors">
          {movie.title}
        </h3>
        <p className="mt-1.5 text-xs text-foreground-500 flex items-center gap-1.5 flex-wrap">
          <span>{movie.year}</span>
          <span className="w-1 h-1 rounded-full bg-background-400"></span>
          <span>{movie.region}</span>
          <span className="w-1 h-1 rounded-full bg-background-400"></span>
          <span>{movie.duration} 分钟</span>
        </p>
        {showSummary && (
          <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">{movie.summary}</p>
        )}
      </div>
    </Link>
  );
}